import { CreditsBreakdown, PromotionalCode } from '@leadgen/models/client'
import { volumeDiscountPolicy } from '../../constants/volumeDiscountPolicy'

type VolumeDiscountTier = Exclude<
  keyof CreditsBreakdown,
  'totalCredits' | 'totalAmount' | 'promotionalCredits'
>

class PricingCalculator {
  private readonly tierOrder: VolumeDiscountTier[] = ['tier1', 'tier2', 'tier3', 'tier4', 'tier5']
  calculateCreditsBreakdownForPurchase = ({
    numberOfCreditsToBuy,
    alreadyPurchasedThisMonth,
    promotionalCode,
  }: {
    numberOfCreditsToBuy: number
    alreadyPurchasedThisMonth: number
    promotionalCode?: PromotionalCode
  }): { amountToPay: number; creditsBreakdown: CreditsBreakdown } => {
    if (numberOfCreditsToBuy <= 0) {
      return { amountToPay: 0, creditsBreakdown: this.createEmptyCreditsBreakdown() }
    }
    let remainingCredits = numberOfCreditsToBuy
    let purchasedThisMonth = Math.max(alreadyPurchasedThisMonth, 0)
    const creditsBreakdown = this.createEmptyCreditsBreakdown()
    let amountToPay = 0
    for (const tier of this.tierOrder) {
      if (remainingCredits <= 0) {
        break
      }
      const { from, to, pencePerCredit } = volumeDiscountPolicy[tier]
      const tierStartIndex = Math.max(purchasedThisMonth, (from || 0) - 1)
      const tierEndIndex = to ?? Infinity
      const tierCapacity = tierEndIndex - tierStartIndex
      if (tierCapacity <= 0) {
        continue
      }
      const creditsInTier =
        tierEndIndex === Infinity ? remainingCredits : Math.min(remainingCredits, tierCapacity)
      creditsBreakdown[tier].totalCredits = creditsInTier
      creditsBreakdown[tier].totalAmount = creditsInTier * pencePerCredit
      creditsBreakdown[tier].pencePerCredit = pencePerCredit
      creditsBreakdown.totalCredits += creditsInTier
      creditsBreakdown.totalAmount += creditsInTier * pencePerCredit
      amountToPay += creditsInTier * pencePerCredit
      purchasedThisMonth += creditsInTier
      remainingCredits -= creditsInTier
    }
    if (promotionalCode?.type === 'percentage-additional-credits') {
      const basePromotionalCredits = Math.floor(
        (creditsBreakdown.totalCredits * promotionalCode.value) / 100,
      )
      creditsBreakdown.promotionalCredits =
        promotionalCode.maxCredits !== undefined
          ? Math.min(basePromotionalCredits, promotionalCode.maxCredits)
          : basePromotionalCredits
    }
    return { amountToPay, creditsBreakdown }
  }
  private createEmptyCreditsBreakdown = (): CreditsBreakdown => ({
    tier1: {
      totalCredits: 0,
      totalAmount: 0,
      pencePerCredit: volumeDiscountPolicy.tier1.pencePerCredit,
    },
    tier2: {
      totalCredits: 0,
      totalAmount: 0,
      pencePerCredit: volumeDiscountPolicy.tier2.pencePerCredit,
    },
    tier3: {
      totalCredits: 0,
      totalAmount: 0,
      pencePerCredit: volumeDiscountPolicy.tier3.pencePerCredit,
    },
    tier4: {
      totalCredits: 0,
      totalAmount: 0,
      pencePerCredit: volumeDiscountPolicy.tier4.pencePerCredit,
    },
    tier5: {
      totalCredits: 0,
      totalAmount: 0,
      pencePerCredit: volumeDiscountPolicy.tier5.pencePerCredit,
    },
    totalCredits: 0,
    totalAmount: 0,
  })
}

export default new PricingCalculator()
